import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const STORAGE_KEY = 'chicodainclusao_a11y_prefs';

const DEFAULT_PREFS = {
  magnifier: false,
  fontSize: 0, // 0 = normal, 1 = large, 2 = xlarge
  highContrast: false,
  dyslexiaFont: false,
  underlineLinks: false,
  readingGuide: false,
  reduceMotion: false,
  ttsEnabled: false,
};

const AccessibilityContext = createContext(null);

export const AccessibilityProvider = ({ children }) => {
  const [prefs, setPrefs] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) return { ...DEFAULT_PREFS, ...JSON.parse(stored) };
    } catch (e) {
      // ignore
    }
    return DEFAULT_PREFS;
  });

  // Persist + apply classes to <html>
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch (e) {
      // ignore
    }

    const html = document.documentElement;
    html.classList.toggle('a11y-high-contrast', prefs.highContrast);
    html.classList.toggle('a11y-dyslexia-font', prefs.dyslexiaFont);
    html.classList.toggle('a11y-underline-links', prefs.underlineLinks);
    html.classList.toggle('a11y-reduce-motion', prefs.reduceMotion);
    html.classList.toggle('a11y-font-large', prefs.fontSize === 1);
    html.classList.toggle('a11y-font-xlarge', prefs.fontSize === 2);
  }, [prefs]);

  const update = useCallback((patch) => {
    setPrefs((prev) => ({ ...prev, ...patch }));
  }, []);

  const toggle = useCallback((key) => {
    setPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const reset = useCallback(() => {
    setPrefs(DEFAULT_PREFS);
  }, []);

  const speak = useCallback((text) => {
    if (!('speechSynthesis' in window) || !text) return;
    const synth = window.speechSynthesis;
    synth.cancel();
    const lang = document.documentElement.lang || 'pt-BR';
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang.startsWith('pt') ? 'pt-BR' : 'en-US';
    utterance.rate = 0.95;
    synth.speak(utterance);
  }, []);

  const stopSpeaking = useCallback(() => {
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
  }, []);

  return (
    <AccessibilityContext.Provider
      value={{ prefs, update, toggle, reset, speak, stopSpeaking }}
    >
      {children}
    </AccessibilityContext.Provider>
  );
};

export const useAccessibility = () => {
  const ctx = useContext(AccessibilityContext);
  if (!ctx) throw new Error('useAccessibility must be used within AccessibilityProvider');
  return ctx;
};
